// Same REST API + JWT as the Expo app. Only the auth token is persisted (localStorage "auth_token",
// identical to the mobile web build). WhatsApp numbers and documents are NEVER persisted anywhere.
export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "auth_token";

let memToken;

export function getToken() {
  if (memToken === undefined) {
    try { memToken = window.localStorage.getItem(TOKEN_KEY) || null; } catch { memToken = null; }
  }
  return memToken;
}

export function setToken(token) {
  memToken = token || null;
  try {
    if (token) window.localStorage.setItem(TOKEN_KEY, token);
    else window.localStorage.removeItem(TOKEN_KEY);
  } catch { /* storage unavailable: keep in memory only */ }
}

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? String(e.msg).replace(/^Value error, /, "") : JSON.stringify(e))).join(" ");
  if (typeof detail === "object" && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export class ApiError extends Error {
  constructor(status, detail) {
    super(formatApiError(detail));
    this.status = status;
    this.detail = detail;
  }
}

// Locked premium actions (HTTP 402 "membership_required") open the paywall globally.
const membershipListeners = new Set();
export function onMembershipRequired(listener) {
  membershipListeners.add(listener);
  return () => membershipListeners.delete(listener);
}
export const isMembershipError = (e) => e instanceof ApiError && !!e.detail && typeof e.detail === "object" && e.detail.code === "membership_required";

function safeJson(text) {
  try { return JSON.parse(text); } catch { return { detail: text }; }
}

async function handle(res) {
  const text = await res.text();
  const data = text ? safeJson(text) : null;
  if (!res.ok) {
    const detail = data?.detail ?? data;
    if (detail && typeof detail === "object" && detail.code === "membership_required") membershipListeners.forEach((l) => l());
    throw new ApiError(res.status, detail);
  }
  return data;
}

export async function api(path, opts = {}) {
  const headers = { "Content-Type": "application/json" };
  if (opts.auth !== false) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  let res;
  try {
    res = await fetch(`${BACKEND_URL}/api${path}`, {
      method: opts.method ?? "GET",
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    });
  } catch {
    throw new ApiError(0, "You appear to be offline. Check your connection and try again.");
  }
  return handle(res);
}

/** Multipart upload of a browser File/Blob (field "file") plus optional text fields. */
export async function uploadFile(path, file, fields = {}, filename) {
  const form = new FormData();
  Object.entries(fields).forEach(([k, v]) => form.append(k, v));
  form.append("file", file, filename || file.name || "upload.jpg");
  const token = getToken();
  let res;
  try {
    res = await fetch(`${BACKEND_URL}/api${path}`, { method: "POST", body: form, headers: token ? { Authorization: `Bearer ${token}` } : undefined });
  } catch {
    throw new ApiError(0, "You appear to be offline. Check your connection and try again.");
  }
  return handle(res);
}

export const photoUri = (p) => (p ? `${BACKEND_URL}${p}` : undefined);
